import React, { UIEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "../../components/Buttons/Button";
import EmptyState from "../../components/EmptyState";
import Sidebar from "../../components/Sidebar";
import { Spinner } from "../../components/Spinner";
import StatusBadge from "../../components/StatusBadge";
import { fetchNews } from "../../services/newsService/newsService";
import type { PaginatedNews } from "../../services/newsService/newsService.types";
import { logger } from "../../utils/logger";
import { CoursesPageContainer } from "../CoursesPage/CoursesPage.styles";
import { CourseChevron } from "../StudentPortal/studentCourses.styles";
import {
  PageHeader,
  PageLead,
  PageTitle,
} from "../StudentPortal/studentPortal.styles";
import {
  Cover,
  CoverPlaceholder,
  HeaderActions,
  LoadingSlot,
  NewBadgeRow,
  NewsList,
  NewsMain,
  NewsRow,
  NewsRowBody,
  NewsShell,
  RowExcerpt,
  RowMeta,
  RowTitle,
  SearchInput,
  SearchRow,
} from "./NewsPage.styles";

const emptyPage: PaginatedNews = {
  count: 0,
  next: null,
  previous: null,
  results: [],
  can_manage: false,
};

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("ru-RU") : "";

const NewsPage: React.FC = () => {
  const [data, setData] = useState<PaginatedNews>(emptyPage);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      fetchNews({ search: search.trim() || undefined, page: 1 })
        .then((response) => {
          if (active) {
            setData(response);
            setPage(1);
          }
        })
        .catch((error) => {
          logger.error("Не удалось загрузить Новости:", error);
          if (active) setData(emptyPage);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [search]);

  const loadNextPage = async () => {
    if (!data.next || loading || loadingMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const response = await fetchNews({
        search: search.trim() || undefined,
        page: nextPage,
      });
      setData((current) => {
        const existingIds = new Set(current.results.map((article) => article.id));
        return {
          ...response,
          can_manage: current.can_manage ?? response.can_manage,
          results: [
            ...current.results,
            ...response.results.filter((article) => !existingIds.has(article.id)),
          ],
        };
      });
      setPage(nextPage);
    } catch (error) {
      logger.error("Не удалось загрузить следующую страницу Новостей:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = (event: UIEvent<HTMLElement>) => {
    const node = event.currentTarget;
    const distanceToBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (distanceToBottom < 280) {
      void loadNextPage();
    }
  };

  return (
    <CoursesPageContainer>
      <Sidebar />
      <NewsMain onScroll={handleScroll}>
        <NewsShell>
          <PageHeader>
            <div>
              <PageTitle>Новости</PageTitle>
              <PageLead>
                Практические разборы, изменения и материалы для школ и преподавателей.
              </PageLead>
            </div>
            {data.can_manage ? (
              <HeaderActions>
                <Button variant="secondary" size="md" onClick={() => navigate("/news/manage")}>
                  Управление
                </Button>
              </HeaderActions>
            ) : null}
          </PageHeader>

          <SearchRow>
            <SearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по новостям"
              aria-label="Поиск по новостям"
            />
          </SearchRow>

          {loading ? (
            <LoadingSlot aria-label="Загрузка публикаций">
              <Spinner $size="2rem" />
            </LoadingSlot>
          ) : data.results.length === 0 ? (
            <EmptyState
              kind={search.trim() ? "filtered" : "empty"}
              title={
                search.trim()
                  ? "По вашему запросу ничего не найдено"
                  : "Опубликованных материалов пока нет."
              }
              description={
                search.trim()
                  ? "Измените поисковый запрос и попробуйте снова."
                  : "Новые статьи появятся здесь после публикации."
              }
            />
          ) : (
            <NewsList>
              {data.results.map((article) => (
                <NewsRow key={article.id} to={"/news/" + article.id}>
                  <Cover>
                    {article.cover ? (
                      <img src={article.cover.url} alt="" loading="lazy" />
                    ) : (
                      <CoverPlaceholder>Без обложки</CoverPlaceholder>
                    )}
                  </Cover>
                  <NewsRowBody>
                    {article.is_new ? (
                      <NewBadgeRow>
                        <StatusBadge text="Новая" variant="brand" appearance="soft" />
                      </NewBadgeRow>
                    ) : null}
                    <RowMeta>
                      {article.number ? <span>№ {article.number}</span> : null}
                      <span>{formatDate(article.display_date)}</span>
                    </RowMeta>
                    <RowTitle>{article.title}</RowTitle>
                    {article.excerpt ? <RowExcerpt>{article.excerpt}</RowExcerpt> : null}
                  </NewsRowBody>
                  <CourseChevron aria-hidden="true" />
                </NewsRow>
              ))}
              {loadingMore ? (
                <LoadingSlot aria-label="Загрузка публикаций">
                  <Spinner $size="2rem" />
                </LoadingSlot>
              ) : null}
            </NewsList>
          )}
        </NewsShell>
      </NewsMain>
    </CoursesPageContainer>
  );
};

export default NewsPage;